<section class="mine-dashboard" [style.height]="data?.height || 'var(--dvm-chart-height-xl, 720px)'">
  <header class="mine-dashboard__header">
    <h3>{{ dashboardTitle }}</h3>
  </header>

  <div *ngIf="loading && !hasError" class="mine-dashboard__loader">
    <mat-progress-spinner mode="indeterminate" diameter="42" strokeWidth="4"></mat-progress-spinner>
  </div>

  <app-data-error *ngIf="hasError" [message]="errorMessage"></app-data-error>

  <div
    *ngIf="!loading && !hasError"
    class="mine-dashboard__layout"
    [class.mine-dashboard__layout--without-tree]="!showTreePanel">
    <aside class="summary-panel">
      <div class="summary-track" aria-hidden="true"></div>

      <div
        class="summary-entry"
        *ngFor="let item of summaryItems; trackBy: trackBySummaryId">
        <button
          type="button"
          class="summary-row"
          [class.summary-row--dropdown]="summaryTreeEnabled && hasSummaryChildren(item)"
          [class.summary-row--selected]="isSummarySelected(item)"
          [class.summary-row--partial]="isSummaryPartial(item)"
          (click)="selectSummaryItem(item)"
          [matTooltip]="item.label"
          matTooltipPosition="right">
          <ng-container *ngIf="getSummaryDropdownNode(item) as summaryNode; else summarySpacer">
            <span class="summary-toggle">
              <mat-icon
                *ngIf="summaryNode.children.length"
                [class.rotated]="isExpanded(summaryNode)">
                chevron_right
              </mat-icon>
            </span>
          </ng-container>
          <ng-template #summarySpacer>
            <span class="summary-toggle"></span>
          </ng-template>
          <span class="summary-label">{{ item.label }}</span>
          <span class="summary-score" [ngStyle]="getSummaryStyle(item)">
            {{ getSummaryScore(item) }}
          </span>
        </button>

        <ng-container *ngIf="getSummaryDropdownNode(item) as summaryNode">
          <div class="summary-children" *ngIf="summaryNode.children.length && isExpanded(summaryNode)" @fadePanel>
            <ng-container *ngFor="let child of summaryNode.children">
              <ng-container
                *ngTemplateOutlet="summaryChildNode; context: { $implicit: child, level: 1 }">
              </ng-container>
            </ng-container>
          </div>
        </ng-container>
      </div>
    </aside>

    <main class="donut-panel">
      <div class="donut-stage">
        <div
          #chartHost
          class="donut-chart"
          [id]="chartDomId"
          role="img"
          [attr.aria-label]="dashboardTitle">
        </div>

        <div class="donut-center-label" *ngIf="centerLabelLines.length">
          <span *ngFor="let line of centerLabelLines">{{ line }}</span>
        </div>

        <article
          class="insight-card"
          *ngFor="let card of infoCards; trackBy: trackByCardId"
          [ngClass]="getInfoCardClass(card)"
          [ngStyle]="getInfoCardStyle(card)"
          @fadePanel>
          <h4>{{ card.title }}</h4>
          <ul>
            <li *ngFor="let item of card.items; trackBy: trackByLabel">
              <span>{{ item.label }}</span>
              <em>:</em>
              <strong>{{ item.value }}</strong>
            </li>
          </ul>
        </article>
      </div>
    </main>

    <aside class="tree-panel" *ngIf="showTreePanel">
      <div class="tree-panel__header">
        <span>{{ treeTitle }}</span>
        <small>{{ visibleNodeCount }}</small>
      </div>

      <div class="tree-search" *ngIf="treeSearchEnabled">
        <mat-icon>search</mat-icon>
        <input
          type="search"
          [placeholder]="treeSearchPlaceholder"
          [value]="searchTerm"
          (input)="onSearchChange($event)" />
        <button
          type="button"
          class="icon-button"
          *ngIf="searchTerm"
          (click)="clearSearch($event)"
          matTooltip="Clear">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <div class="tree-list">
        <div
          class="tree-row"
          *ngFor="let row of visibleNodes; trackBy: trackByNodeId"
          [class.tree-row--selected]="getSelectionState(row.node) === 'checked'"
          [class.tree-row--partial]="getSelectionState(row.node) === 'partial'"
          [style.padding-left.px]="getNodeIndent(row.level)"
          (click)="onNodeRowClick(row.node)"
          @fadePanel>
          <button
            type="button"
            class="icon-button expand-button"
            *ngIf="row.node.children.length"
            (click)="toggleNodeExpansion(row.node, $event)"
            [matTooltip]="isExpanded(row.node) ? 'Collapse' : 'Expand'">
            <mat-icon [class.rotated]="isExpanded(row.node)">chevron_right</mat-icon>
          </button>
          <span class="tree-spacer" *ngIf="!row.node.children.length"></span>

          <input
            type="checkbox"
            class="node-check"
            [checked]="isNodeChecked(row.node)"
            [indeterminate]="isNodePartial(row.node)"
            (click)="$event.stopPropagation()"
            (change)="toggleNodeSelection(row.node, $event)" />

          <span class="tree-label" [matTooltip]="row.node.label" matTooltipPosition="above">
            {{ row.node.label }}
          </span>
          <span class="tree-value">{{ formatNodeValue(row.node) }}</span>
        </div>
      </div>
    </aside>
  </div>
</section>

<ng-template #summaryChildNode let-node let-level="level">
  <button
    type="button"
    class="summary-child-row"
    [class.summary-child-row--selected]="getSelectionState(node) === 'checked'"
    [class.summary-child-row--partial]="getSelectionState(node) === 'partial'"
    [style.padding-left.px]="level * 18"
    (click)="onSummaryChildClick(node, $event)">
    <mat-icon
      class="summary-child-arrow"
      *ngIf="node.children.length"
      [class.rotated]="isExpanded(node)">
      chevron_right
    </mat-icon>
    <span class="summary-child-spacer" *ngIf="!node.children.length"></span>
    <span class="summary-child-label">{{ node.label }}</span>
    <span class="summary-child-value">{{ formatNodeValue(node) }}</span>
  </button>

  <div class="summary-children" *ngIf="node.children.length && isExpanded(node)" @fadePanel>
    <ng-container *ngFor="let child of node.children">
      <ng-container
        *ngTemplateOutlet="summaryChildNode; context: { $implicit: child, level: level + 1 }">
      </ng-container>
    </ng-container>
  </div>
</ng-template>
